import React from 'react';
import Module from 'Components/Core/Module';
import useBrokerageConnections from 'Components/Banking/useBrokerageConnections';
import BrokerageLoginForm from 'Components/Banking/BrokerageLoginForm';
import BrokerageOrderAction from 'Components/Banking/BrokerageOrderAction';
import syncBrokerageNow from 'Components/Banking/syncBrokerageNow';
import { registerBrokerageDataListener } from 'Components/Banking/brokerageDataBus';
import './Brokerage.css';

// Uebersetzungen gemaess offizieller comdirect-Doku (Kapitel 7.2.3 Order).
const ORDER_STATUS_LABELS = {
	PENDING: 'In Bearbeitung',
	OPEN: 'Offen',
	EXECUTED: 'Ausgeführt',
	SETTLED: 'Abgerechnet',
	CANCELLED_USER: 'Gestrichen',
	EXPIRED: 'Verfallen',
	CANCELLED_SYSTEM: 'Abgelehnt',
	CANCELLED_TRADE: 'Storniert',
	UNKNOWN: 'Unbekannt',
	PARTIALLY_EXECUTED: 'Teilausgeführt',
	WAITING: 'Wartend',
};

const ORDER_TYPE_LABELS = {
	MARKET: 'Market',
	LIMIT: 'Limit',
	QUOTE: 'Quote',
	STOP_MARKET: 'Stop Market',
	STOP_LIMIT: 'Stop Limit',
	TRAILING_STOP_MARKET: 'Trailing Stop Market',
	TRAILING_STOP_LIMIT: 'Trailing Stop Limit',
	ONE_CANCELS_OTHER: 'OCO',
	NEXT_ORDER: 'Next Order',
};

// Gruppierung der comdirect-Orderstatus fuer den Filter (Chat vom 2026-07-26)
// - "Offen" sind alle noch nicht endgueltig abgeschlossenen Status, das sind
// auch die einzigen, bei denen Streichen/Aendern ueberhaupt moeglich ist
// (Doku Kapitel 7.1.9: Validierung prueft den aktuellen Orderstatus).
const OPEN_STATUSES = ['PENDING', 'OPEN', 'PARTIALLY_EXECUTED', 'WAITING'];
const EXECUTED_STATUSES = ['EXECUTED', 'SETTLED'];
const CANCELLED_STATUSES = ['CANCELLED_USER', 'CANCELLED_SYSTEM', 'CANCELLED_TRADE', 'EXPIRED'];

const STATUS_FILTERS = [
	{ key: 'ALL', label: 'Alle' },
	{ key: 'OPEN', label: 'Offen' },
	{ key: 'EXECUTED', label: 'Ausgeführt' },
	{ key: 'CANCELLED', label: 'Gestrichen' },
];

function matchesStatusFilter(order, filter) {
	if (filter === 'ALL') return true;
	if (filter === 'OPEN') return OPEN_STATUSES.includes(order.orderStatus);
	if (filter === 'EXECUTED') return EXECUTED_STATUSES.includes(order.orderStatus);
	if (filter === 'CANCELLED') return CANCELLED_STATUSES.includes(order.orderStatus);
	return true;
}

// Status-Farbvariante (Nutzervorgabe 2026-07-26) - dieselbe Gruppierung wie
// beim Filter oben. In der kompakten Zeile nur als kleiner Punkt (spart
// Platz, siehe statusDotClass), in der aufklappbaren Detailansicht als volle,
// sprechende Pille mit Text (siehe statusPillClass).
function statusVariant(orderStatus) {
	if (OPEN_STATUSES.includes(orderStatus)) return 'open';
	if (EXECUTED_STATUSES.includes(orderStatus)) return 'executed';
	if (CANCELLED_STATUSES.includes(orderStatus)) return 'cancelled';
	return 'unknown';
}

function statusPillClass(orderStatus) {
	return 'brokerage-status-pill brokerage-status-pill-' + statusVariant(orderStatus);
}

function statusDotClass(orderStatus) {
	return 'brokerage-status-dot brokerage-status-dot-' + statusVariant(orderStatus);
}

function formatValidity(order) {
	if (order.validityType === 'GTD' && order.validity) {
		// order.validity kommt von comdirect im Format YYYY-MM-DD.
		const [year, month, day] = order.validity.split('-');
		return `${day}.${month}.${year}`;
	}
	if (order.validityType === 'GFD') return 'Tagesgültig';
	return order.validityType || '';
}

// Max. 3 Nachkommastellen (Nutzervorgabe 2026-07-26) - die DB liefert das
// Limit als decimal(18,4)-String (z.B. "78.0000"), unnoetige Praezision fuer
// die Anzeige. parseFloat() entfernt dabei auch ueberzaehlige Nullen (z.B.
// "20.0000" -> "20" statt "20.000").
function formatAmount(amountValue) {
	if (!amountValue || amountValue.value === undefined) return '';
	const rounded = Math.round(parseFloat(amountValue.value) * 1000) / 1000;
	return rounded + (amountValue.unit && amountValue.unit !== 'XXX' ? ' ' + amountValue.unit : '');
}

// Stückzahlen sind immer ganze Anteile (Aktienbruchteile gibt es nicht) - die
// DB-Spalte quantity/openquantity ist entsprechend int(11) (Chat vom
// 2026-07-26). Math.round() bleibt als Absicherung, falls comdirect selbst
// doch mal einen Nachkommawert liefert. Kein "Stk."-Suffix mehr (Nutzervorgabe
// 2026-07-26) - redundant zur Spaltenueberschrift "Stück".
function formatQuantity(amountValue) {
	if (!amountValue || amountValue.value === undefined) return '';
	return String(Math.round(parseFloat(amountValue.value)));
}

// Rueckbau 2026-07-25: Orderbuch ist jetzt eine reine, lokal aufgebaute
// Historie (bankingBrokerageOrders, waechst dauerhaft, unabhaengig davon, wie
// lange comdirect eine Order selbst noch vorhaelt) - keine comdirect-
// Verbindung fuer die reine Ansicht mehr noetig. Aktualisiert wird nur ueber
// das Reload-Icon. Eine neue Order sowie Streichen/Aendern brauchen weiterhin
// eine comdirect-Verbindung (Login-Dialog davor) - die bleibt seit dem Umbau
// vom 2026-07-26 (Nutzervorgabe) danach bestehen, statt sofort widerrufen zu
// werden (siehe BrokerageTask::syncBrokerageData()).
//
// Muss (wie ModuleTable es intern tut) in <Module> gewrappt werden - nur das
// registriert bei x.wam[id].show()/hide() die tatsaechliche Ein-/Ausblende-
// Logik beim Seitenwechsel. Ohne diesen Wrapper bleibt die Seite dauerhaft im
// DOM sichtbar und ueberlappt beim Navigieren mit anderen Modulen.
export default function BrokerageOrderbook(props) {

	const x = props.x;
	const connections = useBrokerageConnections(x);

	const [connectionId, setConnectionId] = React.useState(null);
	const [orders, setOrders] = React.useState(null);
	const [error, setError] = React.useState('');
	const [showSync, setShowSync] = React.useState(false);
	// Default "Offen" (Nutzervorgabe 2026-07-26) - die meist relevanten Orders
	// beim Oeffnen direkt sichtbar, statt erst filtern zu muessen.
	const [statusFilter, setStatusFilter] = React.useState('OPEN');
	const [actionTarget, setActionTarget] = React.useState(null);
	// Aufklappbare Detailansicht je Zeile (Nutzervorgabe 2026-07-26) - zeigt
	// alle Felder uebersichtlich untereinander, unabhaengig davon, welche
	// Spalten die kompakte Zeile gerade (mobil) ausblendet.
	const [expandedOrderId, setExpandedOrderId] = React.useState(null);
	const [lastSyncAt, setLastSyncAt] = React.useState(null);
	// Fortschrittsanzeige fuer das Reload-Icon (Chat vom 2026-07-26) - nur fuer
	// den schnellen Weg ueber eine bereits offene Verbindung (syncBrokerageNow);
	// braucht sie einen neuen Login, uebernimmt der Login-Dialog selbst die
	// Fortschrittsanzeige (siehe BrokerageLoginForm.js).
	const [syncing, setSyncing] = React.useState(false);

	// Erste Verbindung automatisch vorauswaehlen, statt eine unnoetige Auswahl
	// zu erzwingen - umschalten auf eine andere geht danach jederzeit ueber den
	// Verbindungs-Wechsler (switchConnection()).
	React.useEffect(() => {
		if (connectionId === null && connections && connections.length > 0) {
			setConnectionId(connections[0].connectionid);
		}
	}, [connections, connectionId]);

	function switchConnection(id) {
		if (id === connectionId) return;
		setOrders(null);
		setError('');
		setConnectionId(id);
	}

	function loadOrders() {
		x.was.axPost('Backend/webapp.php?module=Banking&task=BrokerageLocalOrderList', { connectionId }, (result) => {
			const content = result.data.content || {};
			if (result.data.returncode !== 1) {
				setError(content.message || 'Orderbuch konnte nicht geladen werden.');
				return;
			}
			setOrders(content.orders || []);
			setLastSyncAt(content.lastSyncAt || null);
		});
	}

	React.useEffect(() => {
		if (connectionId !== null) loadOrders();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	// Auf Synchronisierungen von ANDEREN Brokerage-Seiten reagieren (Chat vom
	// 2026-07-26, Bugreport: Orderbuch zeigte eine woanders ausgefuehrte Order
	// nicht, bis hier separat auf Reload geklickt wurde).
	const loadOrdersRef = React.useRef(loadOrders);
	loadOrdersRef.current = loadOrders;
	React.useEffect(() => {
		return registerBrokerageDataListener(x, () => {
			if (connectionId !== null) loadOrdersRef.current();
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	const connection = connections ? connections.find((c) => c.connectionid === connectionId) : null;
	const filteredOrders = (orders || []).filter((order) => matchesStatusFilter(order, statusFilter));

	let inner;
	if (connections === null) {
		inner = <p>Lade…</p>;
	} else if (connections.length === 0) {
		inner = <p>Keine comdirect-Verbindung vorhanden. Bitte zuerst unter Banking → Konten eine comdirect-Verbindung anlegen.</p>;
	} else if (connectionId === null) {
		inner = (
			<div className="brokerage-picker">
				{connections.map((c) => (
					<div key={c.connectionid} className="brokerage-picker-item" onClick={() => setConnectionId(c.connectionid)}>
						{c.bankname} – {c.accountlabel}
					</div>
				))}
			</div>
		);
	} else {
		inner = (
			<React.Fragment>
				{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
				<div className="brokerage-status-filter">
					{STATUS_FILTERS.map((filter) => (
						<span
							key={filter.key}
							className={'brokerage-status-filter-item' + (statusFilter === filter.key ? ' brokerage-status-filter-active' : '')}
							onClick={() => setStatusFilter(filter.key)}
						>
							{filter.label}
						</span>
					))}
				</div>
				{orders === null && <p>Lade Orderbuch…</p>}
				{orders !== null && orders.length === 0 && <p className="brokerage-hint">Noch keine Orders - bitte einmal synchronisieren.</p>}
				{orders !== null && orders.length > 0 && filteredOrders.length === 0 &&
					<p className="brokerage-hint">Keine Orders in dieser Kategorie.</p>
				}
				{filteredOrders.length > 0 &&
					<div className="brokerage-table-scroll">
						<div className="brokerage-order-row brokerage-order-row-header">
							<span>Wertpapier</span>
							<span>Art</span>
							<span>Richtung</span>
							<span>Stück</span>
							<span>Handelsplatz</span>
							<span>Limit</span>
							<span>Gültigkeit</span>
							<span title="Status">
							<span className="brokerage-status-header-text">Status</span>
							<i className="bi bi-circle-fill brokerage-status-header-icon"></i>
						</span>
							<span></span>
						</div>
						{filteredOrders.map((order, idx) => {
							const cancellable = OPEN_STATUSES.includes(order.orderStatus);
							const expanded = expandedOrderId === order.orderId;
							return (
								<React.Fragment key={order.orderId || idx}>
									<div
										className="brokerage-order-row brokerage-order-row-clickable"
										onClick={() => setExpandedOrderId(expanded ? null : order.orderId)}
									>
										<span className="brokerage-order-instrument">{order.instrumentName || order.instrumentId || 'Order'}</span>
										<span>{ORDER_TYPE_LABELS[order.orderType] || order.orderType || ''}</span>
										<span className={order.side === 'SELL' ? 'brokerage-side-sell' : 'brokerage-side-buy'}>{order.side === 'SELL' ? 'Verkauf' : 'Kauf'}</span>
										<span>{formatQuantity(order.openQuantity || order.quantity)}</span>
										<span>{order.venueName || order.venueId || ''}</span>
										<span>{formatAmount(order.limit)}</span>
										<span>{formatValidity(order)}</span>
										<span className="brokerage-status-dot-cell">
											<span className={statusDotClass(order.orderStatus)} title={ORDER_STATUS_LABELS[order.orderStatus] || order.orderStatus || ''}></span>
										</span>
										<span className="brokerage-order-actions">
											{cancellable &&
												<React.Fragment>
													<i
														className="bi bi-pencil brokerage-action-icon"
														title="Order ändern"
														onClick={(e) => { e.stopPropagation(); setActionTarget({ action: 'change', order }); }}
													></i>
													<i
														className="bi bi-x-circle brokerage-action-icon brokerage-action-sell"
														title="Order streichen"
														onClick={(e) => { e.stopPropagation(); setActionTarget({ action: 'cancel', order }); }}
													></i>
												</React.Fragment>
											}
										</span>
									</div>
									{expanded &&
										<div className="brokerage-order-detail">
											<div className="brokerage-order-detail-row">
												<span className="brokerage-order-detail-label">Wertpapier</span>
												<span>{order.instrumentName || order.instrumentId || 'Order'}</span>
											</div>
											<div className="brokerage-order-detail-row">
												<span className="brokerage-order-detail-label">Art</span>
												<span>{ORDER_TYPE_LABELS[order.orderType] || order.orderType || ''}</span>
											</div>
											<div className="brokerage-order-detail-row">
												<span className="brokerage-order-detail-label">Richtung</span>
												<span className={order.side === 'SELL' ? 'brokerage-side-sell' : 'brokerage-side-buy'}>{order.side === 'SELL' ? 'Verkauf' : 'Kauf'}</span>
											</div>
											<div className="brokerage-order-detail-row">
												<span className="brokerage-order-detail-label">Stück</span>
												<span>
													{formatQuantity(order.quantity)}
													{order.openQuantity && formatQuantity(order.openQuantity) !== formatQuantity(order.quantity)
														? ` (offen: ${formatQuantity(order.openQuantity)})`
														: ''}
												</span>
											</div>
											<div className="brokerage-order-detail-row">
												<span className="brokerage-order-detail-label">Handelsplatz</span>
												<span>{order.venueName || order.venueId || '–'}</span>
											</div>
											<div className="brokerage-order-detail-row">
												<span className="brokerage-order-detail-label">Limit</span>
												<span>{formatAmount(order.limit)}</span>
											</div>
											<div className="brokerage-order-detail-row">
												<span className="brokerage-order-detail-label">Gültigkeit</span>
												<span>{formatValidity(order)}</span>
											</div>
											<div className="brokerage-order-detail-row">
												<span className="brokerage-order-detail-label">Status</span>
												<span className={statusPillClass(order.orderStatus)}>{ORDER_STATUS_LABELS[order.orderStatus] || order.orderStatus || ''}</span>
											</div>
											{cancellable &&
												<div className="brokerage-order-detail-actions">
													<button type="button" className="btn btn-secondary" onClick={() => setActionTarget({ action: 'change', order })}>
														<i className="bi bi-pencil"></i> Ändern
													</button>
													<button type="button" className="btn btn-secondary" onClick={() => setActionTarget({ action: 'cancel', order })}>
														<i className="bi bi-x-circle"></i> Streichen
													</button>
												</div>
											}
										</div>
									}
								</React.Fragment>
							);
						})}
					</div>
				}
			</React.Fragment>
		);
	}

	return (
		<Module id={props.id} x={x} classes="module-brokerage-orderbook" title={props.title} display="d-block">
			<div className="brokerage-toolbar">
				{connections && connections.length > 1 &&
					<div className="brokerage-connection-switch">
						<label htmlFor="brokerage-orderbook-connection-select">Verbindung</label>
						<select
							id="brokerage-orderbook-connection-select"
							value={connectionId || ''}
							onChange={(e) => switchConnection(parseInt(e.target.value, 10))}
						>
							{connections.map((c) => (
								<option key={c.connectionid} value={c.connectionid}>{c.bankname} – {c.accountlabel}</option>
							))}
						</select>
					</div>
				}
				{connectionId !== null &&
					<i
						className={`bi bi-arrow-repeat brokerage-action-icon brokerage-toolbar-reload ${syncing ? 'brokerage-icon-spin' : ''}`}
						title="Jetzt synchronisieren"
						onClick={() => {
							if (syncing) return;
							setSyncing(true);
							setError('');
							syncBrokerageNow(x, connectionId, {
								onDone: () => { setSyncing(false); loadOrders(); },
								onNeedsLogin: () => { setSyncing(false); setShowSync(true); },
								onError: (message) => { setSyncing(false); setError(message); },
							});
						}}
					></i>
				}
				{syncing && <span className="brokerage-hint">Synchronisiere…</span>}
				{!syncing && lastSyncAt && <span className="brokerage-hint">Letzter Sync: {lastSyncAt}</span>}
			</div>
			{inner}
			{showSync &&
				<div className="brokerage-overlay">
					<BrokerageLoginForm
						x={x}
						connectionId={connectionId}
						accountLabel={connection ? connection.bankname + ' – ' + connection.accountlabel : ''}
						pinUse={true}
						pinStored={connection ? parseInt(connection.pinstored, 10) === 1 : false}
						purpose="sync"
						onLoggedIn={() => { setShowSync(false); loadOrders(); }}
						onCancel={() => setShowSync(false)}
					/>
				</div>
			}
			{actionTarget &&
				<div className="brokerage-overlay">
					<BrokerageOrderAction
						x={x}
						connectionId={connectionId}
						accountLabel={connection ? connection.bankname + ' – ' + connection.accountlabel : ''}
						pinStored={connection ? parseInt(connection.pinstored, 10) === 1 : false}
						action={actionTarget.action}
						order={actionTarget.order}
						onCancel={() => setActionTarget(null)}
						onDone={() => { setActionTarget(null); loadOrders(); }}
					/>
				</div>
			}
		</Module>
	)

};
